import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DbPortalStandortListe } from '@gql/shared-types';
import { api, API_BASIS } from '../api/client.js';
import { useDatenKontext } from '../daten/kontext.js';
import { useAuth } from '../auth/AuthKontext.js';
import { FortschrittBalken, PrioMarke } from '../components/Fortschritt.js';
import { normalisiere } from '../daten/ansicht.js';
import { adresse, ZUORDNUNG_TEXT } from '../daten/standorte.js';

export function Standorte() {
  const daten = useDatenKontext();
  const { zustand } = useAuth();
  const admin = zustand.status === 'bereit' && zustand.ich.nutzer?.rolle === 'gql_admin';
  const [liste, setListe] = useState<DbPortalStandortListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [filter, setFilter] = useState<'alle' | 'offen' | 'ohne_gebiet' | 'p1'>('alle');
  const gebietName = useMemo(() => new Map(daten.gebiete.features.map((f) => [f.properties.slug, f.properties.name])), [daten]);

  useEffect(() => {
    api<DbPortalStandortListe>('/standorte')
      .then(setListe)
      .catch((f: Error) => setFehler(f.message));
  }, []);

  const sichtbar = useMemo(() => {
    if (!liste) return [];
    const q = normalisiere(suche.trim());
    return liste.standorte.filter((s) => {
      if (filter === 'offen' && s.fortschritt.vollstaendig) return false;
      if (filter === 'ohne_gebiet' && s.gebietSlug) return false;
      if (filter === 'p1' && s.prioritaet !== 1) return false;
      if (q && !normalisiere(`${s.name} ${s.ort ?? ''} ${s.externeId ?? ''}`).includes(q)) return false;
      return true;
    });
  }, [liste, suche, filter]);

  return (
    <div className="seite">
      <div className="titelzeile">
        <h1>{admin ? 'Standorte (alle Nutzer)' : 'Meine Standorte'}</h1>
        <div className="knopfreihe">
          {liste && liste.standorte.length > 0 && (
            <a href={`${API_BASIS}/export.xlsx`} className="knopf-sekundaer knopf-auto" download>
              Als Excel herunterladen
            </a>
          )}
          <Link to="/standorte/import" className="knopf">
            Standorte importieren
          </Link>
        </div>
      </div>
      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
      {!liste && !fehler && (
        <p role="status">
          <span className="spinner" aria-hidden="true" /> Standorte werden geladen …
        </p>
      )}
      {liste && liste.standorte.length === 0 && (
        <div className="kasten leer-kasten">
          <h2>Noch keine Standorte</h2>
          <p>
            Laden Sie Ihre Standortliste als CSV hoch (Vorlage im Import). Das Portal ordnet jeden Standort einem
            Konzessionsgebiet zu und zeigt die benötigten Unterlagen. Mindestpaket: ein Vertrag und eine Rechnung je
            Standort und Jahr 2010–2020.
          </p>
          <Link to="/standorte/import" className="knopf">
            Zum Import
          </Link>
        </div>
      )}
      {liste && liste.standorte.length > 0 && (
        <>
          <div className="kennzahlen">
            <div className="kennzahl">
              <span className="kennzahl-label">Standorte</span>
              <span className="kennzahl-wert">{liste.zusammenfassung.standorte}</span>
            </div>
            <div className="kennzahl">
              <span className="kennzahl-label">vollständig</span>
              <span className="kennzahl-wert">
                {liste.zusammenfassung.vollstaendig} von {liste.zusammenfassung.standorte}
              </span>
            </div>
            <div className="kennzahl">
              <span className="kennzahl-label">ohne Gebiet</span>
              <span className="kennzahl-wert">{liste.zusammenfassung.ohneGebiet}</span>
            </div>
          </div>
          <div className="filterzeile">
            <label className="feld">
              <span className="feld-label">Suchen</span>
              <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name, Ort oder ID" />
            </label>
            <label className="feld">
              <span className="feld-label">Anzeigen</span>
              <select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
                <option value="alle">alle</option>
                <option value="p1">nur Priorität 1</option>
                <option value="offen">nur unvollständige</option>
                <option value="ohne_gebiet">nur ohne Gebiet</option>
              </select>
            </label>
          </div>
          <p className="hinweis-klein">
            Priorität: P1 = Gebiete mit Bosch als Konzessionär, P2 = Berlin, P3 = übrige. Bitte mit P1 beginnen.
          </p>
          <ul className="standortliste" aria-label="Standorte">
            {sichtbar.map((s) => (
              <li key={s.id}>
                <Link to={`/standorte/${s.id}`} className="standort-eintrag">
                  <PrioMarke p={s.prioritaet} />
                  <span className="standort-text">
                    <span className="standort-name">{s.name}</span>
                    <span className="standort-detail">
                      {adresse(s)}
                      {(() => {
                        const g = s.gebietSlug ? (gebietName.get(s.gebietSlug) ?? s.gebietSlug) : ZUORDNUNG_TEXT.offen;
                        return g === s.ort ? null : ` · Gebiet ${g}`;
                      })()}
                    </span>
                  </span>
                  <FortschrittBalken f={s.fortschritt} />
                </Link>
              </li>
            ))}
          </ul>
          {sichtbar.length === 0 && <p className="leer">Keine Standorte für diese Auswahl.</p>}
        </>
      )}
    </div>
  );
}
